import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { SwiperSlide } from 'swiper/react';

import Document from '@components/drive/Document';
import styles from '@components/drive/Drive.module.css';
import DriveSearch from '@components/drive/DriveSearch';
import Slides from '@components/Slides';
import Icon from '@components/text/Icon';
import BodyCopy from '@components/typography/BodyCopy';
import ButtonLink from '@components/typography/ButtonLink';
import Heading2 from '@components/typography/Heading2';
import { FileResourceFragment, GetJourneyWithDocumentsQuery } from '@typing/Generated';
import { sortByTimestamp } from '@utils/modelUtils';

type Journey = GetJourneyWithDocumentsQuery['journey'];

type Props = {
  handleFileResourceCreate: () => void;
  handlePreviewFileResource: (fileResource: FileResourceFragment) => void;
  journey: Journey;
};

const DriveHeader = ({ handleFileResourceCreate, handlePreviewFileResource, journey }: Props) => {
  const recentFileResources = useMemo(
    () =>
      sortByTimestamp(
        journey.fileResources.filter(resource => resource.attachment),
        documents => documents.updatedAt
      ).slice(0, 4),
    [journey.fileResources]
  );

  /* eslint-disable @typescript-eslint/naming-convention */

  const breakPoints = {
    1: {
      slidesPerView: 1.3
    },
    576: {
      slidesPerView: 2.4
    },
    767: {
      slidesPerView: 2.4
    }
  };
  /* eslint-enable @typescript-eslint/naming-convention */

  return (
    <div className={styles.header}>
      <div>
        <div className={styles.titleAndSearch}>
          <div className={styles.noWrap}>
            <Heading2>
              <FormattedMessage id="pages.drive.recentFiles" />
            </Heading2>
          </div>
          <DriveSearch journey={journey} />
        </div>
        <div className={styles.headerRecentFiles}>
          {recentFileResources.map(resource => (
            <Document
              fileResource={resource}
              handlePreviewFileResource={handlePreviewFileResource}
              journey={journey}
              key={resource.id}
            />
          ))}
        </div>

        <div className={styles.showMobile + ' ' + styles.recentSlides}>
          <Slides breakpoints={breakPoints} hideNavigation={recentFileResources.length <= 4} spaceBetween={21}>
            {recentFileResources.map(resource => (
              <SwiperSlide key={resource.id}>
                <Document
                  fileResource={resource}
                  handlePreviewFileResource={handlePreviewFileResource}
                  journey={journey}
                />
              </SwiperSlide>
            ))}
          </Slides>
        </div>
      </div>
      <ButtonLink onClick={handleFileResourceCreate} stylized={false}>
        <div className={styles.uploadDoc}>
          <div>
            <Icon color="primary" icon="add" />
            <div className={styles.uploadDocText}>
              <BodyCopy color="primary700">
                <FormattedMessage id="pages.drive.uploadDocument" />
              </BodyCopy>
            </div>
          </div>
        </div>
      </ButtonLink>
    </div>
  );
};

export default DriveHeader;
